// noinspection JSUnusedGlobalSymbols

import { alpha, darken, Shadows, ThemeOptions } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import type { PaletteColorOptions, PaletteOptions } from '@mui/material/styles/createPalette';
import type { TypographyOptions } from '@mui/material/styles/createTypography';
import type { Properties } from 'csstype';

import '@fontsource/open-sans/300.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';

const palette = {
  primary: {
    50: '#F6FBDB',
    100: '#EDF9DB',
    200: '#4F9E30',
    300: '#067013',
    main: '#4F9E30',
  },
  success: {
    main: '#067013',
  },
  warning: {
    light: '#ffeed0',
    main: '#FF861D',
  },
  error: {
    main: '#FF0A11',
  },
  grey: {
    50: '#f5f6f9',
    100: '#eaf0f4',
    200: '#E0E0E0',
    300: '#91989d',
    400: '#666666',
  },
  blueGrey: {
    main: '#69798c',
    100: '#69798c',
    200: '#36475a',
  },
  common: {
    white: '#fff',
    black: '#000',
  },
  background: {
    default: '#f5f6f9',
    paper: '#fff',
  },
  divider: '#c8cbce',
  charts: {
    colors: ['#17261e', '#802922', '#5d8a66', '#6930be', '#e2bdf2', '#fc809f', '#2baeff', '#7ec4bc', '#ffcd1e'],
  },
  table: {
    cellBackground: '#f5f6f9',
    cellWeekendBackground: '#eceff8',
    headerBackground: '#fff',
    headerWeekendBackground: '#f6f8fe',
    overviewBackground: '#fff',
    overviewWeekendBackground: '#f6f8fe',
    highlightBackground: '#666666',
    highlightWeekendBackground: '#727a80',
    bar: '#94c11f',
    expanderBackground: '#f5f6f9',
    expanderActiveBackground: '#fff',
  },
} satisfies PaletteOptions;

const fontFamily = '"Open Sans", Roboto, Helvetica, Arial, sans-serif';
const fontWeightLight = 300;
const fontWeightRegular = 400;
const fontWeightMedium = 500;
const fontWeightSemiBold = 600;
const fontWeightBold = 700;

const typography: TypographyOptions = {
  fontFamily,
  fontSize: 12,
  fontWeightLight,
  fontWeightRegular,
  fontWeightMedium,
  fontWeightSemiBold,
  fontWeightBold,

  h1: {
    fontFamily,
    fontSize: '34px',
    fontWeight: fontWeightRegular,
  },
  h2: {
    fontFamily,
    fontSize: '24px',
    fontWeight: fontWeightRegular,
  },
  h3: {
    fontFamily,
    fontSize: '20px',
    fontWeight: fontWeightSemiBold,
  },
  h4: {
    fontFamily,
    fontSize: '20px',
    fontWeight: fontWeightRegular,
  },
  h5: {
    fontFamily,
    fontSize: '16px',
    fontWeight: fontWeightSemiBold,
  },
  h6: undefined,
  caption: undefined,
  overline: undefined,
  subtitle1: {
    fontFamily,
    fontSize: '14px',
    fontWeight: fontWeightRegular,
  },
  subtitle2: undefined,
  body1: {
    fontFamily,
    fontSize: '16px',
    fontWeight: fontWeightRegular,
  },
  body2: {
    fontFamily,
    fontSize: '14px',
    fontWeight: fontWeightRegular,
  },
  body3: {
    fontFamily,
    fontSize: '14px',
    fontWeight: fontWeightSemiBold,
  },
  body4: {
    fontFamily,
    fontSize: '14px',
    fontWeight: fontWeightBold,
  },
  body5: {
    fontFamily,
    fontSize: '12px',
    fontWeight: fontWeightRegular,
  },
  body6: {
    fontFamily,
    fontSize: '12px',
    fontWeight: fontWeightSemiBold,
  },
  button: {
    fontFamily,
    fontSize: '14px',
    fontWeight: fontWeightSemiBold,
  },
  button2: {
    fontFamily,
    fontSize: '12px',
    fontWeight: fontWeightSemiBold,
  },
};

const tableHeaderWidth = 200;
const tableHeaderHeight = 40;
const tableCellWidth = 170;
const tableCellHeight = 40;

const themeTemplate = createTheme();

const themeConfig: ThemeOptions = {
  palette,
  typography,
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  shadows: [
    'none',
    '0px 2px 2px rgba(0, 0, 0, 0.2)',
    '0px 4px 4px rgba(0, 0, 0, 0.25)',
    ...themeTemplate.shadows.slice(3),
  ] as Shadows,
  components: {
    MuiTypography: {
      styleOverrides: {
        h1: typography.h1,
        h2: typography.h2,
        h3: typography.h3,
        h4: typography.h4,
        h5: typography.h5,
        h6: typography.h6,
        caption: typography.caption,
        subtitle1: typography.subtitle1,
        subtitle2: typography.subtitle2,
        body1: typography.body1,
        body2: typography.body2,
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          height: '40px',
          textTransform: 'none',
          lineHeight: 'unset',
          padding: '0 10px',
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        variant: 'outlined',
        disableElevation: true,
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: palette.primary.main,
          '&:hover': {
            textDecorationColor: palette.primary.main,
          },
        },
        underlineAlways: {
          textDecorationColor: alpha(palette.primary.main, 0.4),
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardWarning: {
          backgroundColor: palette.warning.light,
          borderColor: palette.warning.main,
          '& svg': {
            color: palette.warning.main,
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        gutters: {
          paddingLeft: '8px',
          paddingRight: '8px',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column-reverse',
        },
        primary: {
          color: '#555555',
          fontWeight: typography.fontWeightSemiBold,
        },
        secondary: {
          color: '#91989d',
          fontWeight: typography.fontWeightRegular,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: '0px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          height: '70px',
          position: 'relative',
          '&:not(:last-child):after': {
            content: '""',
            position: 'absolute',
            height: '1px',
            bottom: '0px',
            left: '16px',
            right: '0px',
            backgroundColor: palette.divider,
          },
          '&.Mui-selected': {
            backgroundColor: palette.background.default,
            '&:hover, &:focus': {
              backgroundColor: darken(palette.background.default, 0.1),
            },
          },
          '&:hover, &:focus': {
            backgroundColor: darken(palette.background.paper, 0.1),
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          width: 360,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '.8rem',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          borderBottomStyle: 'solid',
          borderBottomWidth: 1,
          borderBottomColor: '#e7e7e7',
        },
        title: typography.h5,
        subheader: {
          fontSize: '.75rem',
          marginTop: '.2rem',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          tableLayout: 'fixed',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderWidth: '1.5px',
          borderStyle: 'solid',
          borderColor: '#fff',
          boxSizing: 'border-box',
          height: tableCellHeight,
          padding: 0,
        },
        head: {
          height: tableHeaderHeight,
          padding: '0 8px',
          backgroundColor: '#fff',
          position: 'sticky',
          top: 0,
          textAlign: 'center',
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        variant: 'small',
      },
      variants: [
        {
          props: { variant: 'small' },
          style: { '& .MuiPaper-root': { width: '432px' } },
        },
        {
          props: { variant: 'medium' },
          style: { '& .MuiPaper-root': { width: '664px' } },
        },
        {
          props: { variant: 'large' },
          style: { '& .MuiPaper-root': { width: '896px' } },
        },
      ],
      styleOverrides: {
        paper: {
          padding: '20px',
          maxWidth: '100vw',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          height: '45px',
          padding: '4px 34px 0 10px',
          ...typography.h3,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 10px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          display: 'flex',
          justifyContent: 'space-between',
          margin: '0 10px',
          padding: '16px 0 10px',
          borderTop: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          opacity: '1',
          minWidth: 'auto',
          textTransform: 'none',
          fontWeight: typography.fontWeightRegular,
          backgroundColor: darken(palette.background.default, 0.05),
          transition: themeTemplate.transitions.create('background-color'),

          '&:hover': {
            backgroundColor: darken(palette.background.default, 0.1),
          },

          '&.Mui-selected': {
            fontWeight: typography.fontWeightBold,
            backgroundColor: palette.background.paper,
            '&:hover': {
              backgroundColor: darken(palette.background.paper, 0.05),
            },
          },

          ...Object.fromEntries(
            Array.from({ length: 25 }, (_, i) => [
              `&:nth-of-type(${i + 1})`,
              {
                zIndex: 25 - i,
              },
            ]),
          ),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          display: 'none',
        },
      },
      defaultProps: {
        scrollButtons: true,
        variant: 'scrollable',
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.blueGrey[200],
          padding: '16px',
          ...typography.body5,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          ...typography.body2,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          ...typography.body2,
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          ...typography.body2,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          boxSizing: 'content-box',
          width: '55px',
          height: '18px',
          padding: '8px 0',
          overflow: 'visible',
        },
        switchBase: {
          margin: 0,
          padding: 0,
          '&.Mui-checked': {
            transform: 'translateX(21px)',
            color: palette.background.paper,
            '&.Mui-disabled .MuiSwitch-thumb': {
              color: palette.grey[100],
            },
            '& .MuiSwitch-thumb:before': {
              backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
                palette.primary.main,
              )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
            },
            '& + .MuiSwitch-track': {
              backgroundColor: palette.primary.main,
            },
          },
        },
        track: {
          borderRadius: '9px',
          backgroundColor: palette.grey[200],
          opacity: '1 !important',
        },
        thumb: {
          width: '34px',
          height: '34px',
          borderRadius: '17px',
          boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.15), 0 0 4px 0 rgba(0, 0, 0, 0.1)',
          '&:before': {
            content: "''",
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
              palette.common.black,
            )}" d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>')`,
          },
        },
      },
    },
  },
  mixins: {
    toolbar: {
      minHeight: '64px',
    },
  },
  sizes: {
    tabs: {
      containerHeight: 90,
    },
    table: {
      barWidth: 6,
      headerWidth: tableHeaderWidth,
      headerHeight: tableHeaderHeight,
      cellWidth: tableCellWidth,
      cellHeight: tableCellHeight,
    },
  },
};

declare module '@mui/material' {
  interface PaletteColor {
    50?: string;
    100?: string;
    200?: string;
    300?: string;
  }

  interface SimplePaletteColorOptions {
    50?: string;
    100?: string;
    200?: string;
    300?: string;
  }
  interface PaletteOptions {
    primary?: PaletteColorOptions;
    blueGrey: PaletteColorOptions;
    charts: { colors: string[] };
    // @deprecated
    table: {
      cellBackground: string;
      cellWeekendBackground?: string;
      headerBackground: string;
      headerWeekendBackground?: string;
      overviewBackground: string;
      overviewWeekendBackground?: string;
      overviewActiveBackground?: string;
      overviewActiveWeekendBackground?: string;
      highlightBackground: string;
      highlightWeekendBackground?: string;
      bar: string;
      barActive?: string;
      expanderBackground: string;
      expanderActiveBackground?: string;
    };
  }

  interface Palette {
    primary: PaletteColor;
    blueGrey: PaletteColor;
    charts: {
      colors: string[];
    };
    // @deprecated
    table: {
      cellBackground: string;
      cellWeekendBackground?: string;
      headerBackground: string;
      headerWeekendBackground?: string;
      overviewBackground: string;
      overviewWeekendBackground?: string;
      overviewActiveBackground?: string;
      overviewActiveWeekendBackground?: string;
      highlightBackground: string;
      highlightWeekendBackground?: string;
      bar: string;
      barActive?: string;
      expanderBackground: string;
      expanderActiveBackground?: string;
    };
  }

  interface ThemeOptions {
    sizes: {
      tabs: {
        containerHeight: number;
      };
      table: {
        barWidth: number;
        headerWidth: number;
        headerHeight: number;
        cellWidth: number;
        cellHeight: number;
      };
    };
  }

  interface Theme {
    sizes: {
      tabs: {
        containerHeight: number;
      };
      footer: {
        height: number;
      };
      table: {
        barWidth: number;
        headerWidth: number;
        headerHeight: number;
        cellWidth: number;
        cellHeight: number;
      };
    };
  }

  interface TypographyVariantOptions {
    body3?: Properties;
    body4?: Properties;
    body5?: Properties;
    body6?: Properties;
    button2?: Properties;
  }

  interface TypographyVariants {
    body3: Properties;
    body4: Properties;
    body5: Properties;
    body6: Properties;
    button2: Properties;
  }

  interface TypographyPropsVariantOverrides {
    h6: false;
    subtitle2: false;
    body3: true;
    body4: true;
    body5: true;
    body6: true;
    button2: true;
    caption: false;
    overline: false;
  }
}

declare module '@mui/material/styles/createTypography' {
  interface TypographyOptions {
    body3: TypographyStyleOptions;
    body4: TypographyStyleOptions;
    body5: TypographyStyleOptions;
    body6: TypographyStyleOptions;
    button2: TypographyStyleOptions;
    fontWeightSemiBold: number;
  }
}

declare module '@mui/material/Dialog' {
  interface DialogProps {
    variant?: 'small' | 'medium' | 'large';
  }
}

export const e2mTheme = createTheme(themeConfig);
